import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { config } from "@/config";
import { apiFetch } from "@/lib/api-client";
import {
  NodeChangeLog,
  NodeChangeLogsPage,
  NodeChangeLogsResponse,
} from "@/types/history";

export type NodeChangeLogsSortOrder = "asc" | "desc";

interface GetNodeChangeLogsArgs {
  projectUuid: string;
  label?: string;
  page: number;
  limit: number;
  sortOrder: NodeChangeLogsSortOrder;
  headers: Record<string, string>;
}

const getNodeChangeLogs = async ({
  projectUuid,
  label,
  page,
  limit,
  sortOrder,
  headers,
}: GetNodeChangeLogsArgs): Promise<NodeChangeLogsPage> => {
  const params = new URLSearchParams({
    "filters[projectUuid][$eq]": projectUuid,
    page: String(page),
    limit: String(limit),
    sortName: "createdAt",
    sortOrder,
  });
  if (label) {
    params.append("filters[label][$eq]", label);
  }
  const url = `${config.isometricApiUrl}/node-change-logs?${params.toString()}`;

  const response = await apiFetch(url, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch node change logs: ${response.statusText}`
    );
  }

  const result: NodeChangeLog[] | NodeChangeLogsResponse = await response.json();

  if (Array.isArray(result)) {
    return { data: result, total: result.length };
  }
  const data = result.data ?? result.logs ?? [];
  const total = result.total ?? result.count ?? data.length;
  return { data, total };
};

export default function useGetNodeChangeLogsService(
  projectUuid: string | undefined,
  enabled: boolean = true,
  label?: string,
  page: number = 1,
  limit: number = 10,
  sortOrder: NodeChangeLogsSortOrder = "desc",
  // never polls by default: node_change_logs is the largest table (1.9 GB), so the history is read on open and on
  // window focus, not on a timer
  refetchInterval: number | false = false
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: [
      "node-change-logs",
      projectUuid,
      label ?? "all",
      page,
      limit,
      sortOrder,
    ],
    queryFn: () =>
      getNodeChangeLogs({
        projectUuid: projectUuid!,
        label,
        page,
        limit,
        sortOrder,
        headers: getAuthHeaders(),
      }),
    enabled: !!projectUuid && enabled,
    placeholderData: keepPreviousData,
    refetchInterval,
    refetchIntervalInBackground: false,
  });
}
